import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { listCalls, logCall } from '../../../api/calls';
import { CommDirection } from '../../../types/communications';

interface CallsTabProps {
  leadId: number;
  phone: string;
}

export const CallsTab = ({ leadId, phone }: CallsTabProps) => {
  const queryClient = useQueryClient();
  const [direction, setDirection] = useState<CommDirection>('OUTBOUND');
  const [durationSeconds, setDurationSeconds] = useState('');
  const [disposition, setDisposition] = useState('');
  const [notes, setNotes] = useState('');

  const callsQuery = useQuery({ queryKey: ['calls', leadId], queryFn: () => listCalls(leadId) });

  const logMutation = useMutation({
    mutationFn: () =>
      logCall({
        leadId,
        direction,
        phoneNumber: phone,
        durationSeconds: durationSeconds ? Number(durationSeconds) : undefined,
        disposition: disposition || undefined,
        notes: notes || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calls', leadId] });
      queryClient.invalidateQueries({ queryKey: ['lead-timeline', leadId] });
      setDurationSeconds('');
      setDisposition('');
      setNotes('');
    },
  });

  return (
    <Box sx={{ mt: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Log a call to {phone}
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Direction"
              size="small"
              sx={{ width: 160 }}
              value={direction}
              onChange={(e) => setDirection(e.target.value as CommDirection)}
            >
              <MenuItem value="OUTBOUND">Outbound</MenuItem>
              <MenuItem value="INBOUND">Inbound</MenuItem>
            </TextField>
            <TextField
              label="Duration (seconds)"
              type="number"
              size="small"
              value={durationSeconds}
              onChange={(e) => setDurationSeconds(e.target.value)}
            />
            <TextField
              label="Disposition"
              size="small"
              placeholder="Answered, No answer…"
              value={disposition}
              onChange={(e) => setDisposition(e.target.value)}
            />
          </Stack>
          <TextField label="Notes" multiline minRows={2} fullWidth value={notes} onChange={(e) => setNotes(e.target.value)} />
          <Button
            variant="contained"
            sx={{ alignSelf: 'flex-start' }}
            disabled={logMutation.isPending}
            onClick={() => logMutation.mutate()}
          >
            {logMutation.isPending ? 'Saving…' : 'Log call'}
          </Button>
        </Stack>
      </Paper>

      <Stack spacing={1.5}>
        {callsQuery.data?.data.map((c) => (
          <Paper key={c.id} variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2">
              {c.direction} call — {c.createdBy.firstName} {c.createdBy.lastName}
              {c.durationSeconds ? ` · ${c.durationSeconds}s` : ''}
              {c.disposition ? ` · ${c.disposition}` : ''}
            </Typography>
            {c.notes && <Typography variant="body2">{c.notes}</Typography>}
            <Typography variant="caption" color="text.secondary">
              {new Date(c.createdAt).toLocaleString()}
            </Typography>
          </Paper>
        ))}
        {callsQuery.data?.data.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No calls logged yet.
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
